import React, { useState, useEffect } from 'react';
import { Eye, LogOut } from 'lucide-react';

export default function ImpersonationBanner() {
  const [impersonator, setImpersonator] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    const impStr = localStorage.getItem('impersonator_admin');
    const userStr = localStorage.getItem('user');
    if (impStr && userStr) {
      try {
        setImpersonator(JSON.parse(impStr));
        setCurrentUser(JSON.parse(userStr));
      } catch (e) {}
    } else {
      setImpersonator(null);
      setCurrentUser(null);
    }
  }, []);

  // Fetch all users so admin can switch to other users directly from the banner
  useEffect(() => {
    if (!impersonator) return;
    fetch('/api/users')
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          setAllUsers(data.data || []);
        }
      })
      .catch(err => console.error('Error fetching users in ImpersonationBanner:', err));
  }, [impersonator]);

  if (!impersonator || !currentUser) return null;

  const handleExitImpersonation = () => {
    // Restore original admin
    localStorage.setItem('user', JSON.stringify(impersonator));
    localStorage.removeItem('impersonator_admin');

    // Log the end of impersonation
    try {
      fetch('/api/log_activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: impersonator.google_email,
          action_type: 'IMPERSONATE_END',
          details: `Admin stopped viewing as: ${currentUser.google_email} and returned to admin account`
        })
      }).catch(e => {});
    } catch(e) {}

    // Navigate back to Admin Panel
    window.location.href = '/admin';
  };

  const handleQuickSwitch = (targetUserId) => {
    if (!targetUserId || String(targetUserId) === String(currentUser.id)) return;
    
    // If switching back to the admin themself
    if (String(targetUserId) === String(impersonator.id)) {
      handleExitImpersonation();
      return;
    }

    const targetUser = allUsers.find(u => String(u.id) === String(targetUserId));
    if (!targetUser) return;

    setIsSwitching(true);
    const impersonatedData = {
      id: targetUser.id,
      google_email: targetUser.google_email,
      role: targetUser.role,
      team_id: targetUser.team_id,
      team_name: targetUser.team_name || '',
      sales_code: targetUser.sales_code || '',
      name: targetUser.google_email ? targetUser.google_email.split('@')[0] : 'User'
    };

    localStorage.setItem('user', JSON.stringify(impersonatedData));

    try {
      fetch('/api/log_activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: impersonator.google_email,
          action_type: 'IMPERSONATE_SWITCH',
          details: `Admin switched view from ${currentUser.google_email} to ${targetUser.google_email}`
        })
      }).catch(e => {});
    } catch(e) {}

    window.location.reload();
  };

  const roleLabel = 
    currentUser.role === 'super_admin' ? 'Super Admin' :
    currentUser.role === 'admin' ? 'Admin' :
    currentUser.role === 'sale_manager' ? 'Sale Manager' : 'Sale';

  return (
    <div 
      style={{
        backgroundColor: '#9a3412',
        backgroundImage: 'linear-gradient(90deg, #9a3412 0%, #c2410c 100%)',
        color: '#ffffff',
        padding: '0.25rem 1rem',
        minHeight: '34px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.75rem',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
        borderBottom: '1px solid #fdba74',
        position: 'sticky',
        top: 0,
        zIndex: 9999,
        fontSize: '0.8rem',
        flexShrink: 0,
        whiteSpace: 'nowrap'
      }}
    >
      {/* Left: Compact single line info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <Eye size={15} style={{ color: '#fed7aa', flexShrink: 0 }} />
        <span style={{ color: '#fed7aa', fontSize: '0.78rem' }}>กำลังจำลองมุมมองเป็น:</span>
        <strong style={{ color: '#ffffff', fontSize: '0.82rem', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
          {currentUser.google_email}
        </strong>
        <span 
          style={{
            backgroundColor: 'rgba(0,0,0,0.25)',
            color: '#ffedd5',
            padding: '0.05rem 0.4rem',
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: 600,
            border: '1px solid rgba(255,255,255,0.15)'
          }}
        >
          {roleLabel}{currentUser.team_name ? ` • ทีม ${currentUser.team_name}` : ''}{currentUser.sales_code ? ` (${currentUser.sales_code})` : ''}
        </span>
      </div>

      {/* Right: Quick switcher & Exit button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexShrink: 0 }}>
        {allUsers.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <span style={{ fontSize: '0.72rem', color: '#fed7aa' }}>สลับ:</span>
            <select
              value={currentUser.id || ''}
              onChange={(e) => handleQuickSwitch(e.target.value)}
              disabled={isSwitching}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                color: '#1e293b',
                border: '1px solid #fdba74',
                borderRadius: '4px',
                padding: '0.15rem 0.35rem',
                fontSize: '0.72rem',
                fontWeight: 600,
                cursor: 'pointer',
                outline: 'none',
                maxWidth: '160px',
                height: '24px'
              }}
            >
              {allUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.google_email.split('@')[0]} ({u.role}{u.team_name ? ` - ${u.team_name}` : ''})
                </option>
              ))}
            </select>
          </div>
        )}

        <button
          onClick={handleExitImpersonation}
          style={{
            backgroundColor: '#ffffff',
            color: '#c2410c',
            border: 'none',
            borderRadius: '4px',
            padding: '0.2rem 0.65rem',
            fontSize: '0.75rem',
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
            transition: 'all 0.15s ease',
            height: '24px',
            whiteSpace: 'nowrap'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#fff7ed';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#ffffff';
          }}
          title="ออกจากโหมดจำลองและกลับสู่บัญชีผู้ดูแลระบบ"
        >
          <LogOut size={12} />
          กลับสู่ปกติ
        </button>
      </div>
    </div>
  );
}
